import { metricSpec, zWeightUnit, } from '@strength/domain';
import { z } from 'zod';
import { invalidValue, isValidNumber, parseIntNoNaN, parseMetricValue, parseExactOrRange, toDisplay, } from './util';
import { tKey } from '@strength/i18n';
export * from './util';
export * from './types';
export * from './display';
/**
 * Get a z schema spec to parse a metric input
 */
export function getMetricSpec({ type, config, }) {
    if (type === 'custom') {
        if (!config) {
            throw new Error('Cannot get custom metric spec without config');
        }
        return makeUnitMetricSpec(config);
    }
    if (type === 'reps') {
        if (!config) {
            throw new Error('Cannot get reps metric spec without config');
        }
        return makeRepsMetricSpec(config);
    }
    // Unlike `custom`/`reps`, `intensity` has always worked without a config (its
    // percentage/weight shape is hardcoded, not config-driven), and some call sites
    // (e.g. BenchmarksDataTable, the actuals-worker) still never pass one. Config is
    // purely additive here: thread it through when present to enable keyword parsing,
    // otherwise fall back to the static, config-less spec so every existing caller
    // stays unaffected.
    if (type === 'intensity' && config) {
        return makeIntensityMetricSpec(config);
    }
    return WorkoutSetMetricSpecs[type];
}
const WorkoutSetMetricSpecs = {
    intensity: metricSpec({
        schema: z.string().transform(transformIntensity),
        displayValue: toDisplay,
    }),
    rpe: metricSpec({
        schema: z.string().transform(transformRPE),
        displayValue: toDisplay,
    }),
    tempo: metricSpec({
        schema: z
            .string()
            .transform(transformTempo)
            .refine((i) => i !== null),
        displayValue: (tempo) => {
            if (tempo === '')
                return '';
            const { eccentric, pauseBottom, concentric } = tempo;
            if (tempo.type === 'exact-4') {
                return `${eccentric}-${pauseBottom}-${concentric}-${tempo.pauseTop}`;
            }
            return `${eccentric}-${pauseBottom}-${concentric}`;
        },
    }),
    sets: metricSpec({
        schema: z.string().transform(transformSets),
        displayValue: toDisplay,
    }),
};
/**
 * Create a standard metric spec that handles units and exact or range values
 */
export function makeUnitMetricSpec(config) {
    return metricSpec({
        schema: z
            .string()
            .transform((input, ctx) => parseMetricValue(input, config, ctx))
            .refine((i) => i !== null),
        displayValue: toDisplay,
    });
}
function makeRepsMetricSpec(config) {
    return metricSpec({
        schema: z
            .string()
            .transform((input, ctx) => transformRep(input, ctx, config)),
        displayValue: toDisplay,
    });
}
function makeIntensityMetricSpec(config) {
    return metricSpec({
        schema: z
            .string()
            .transform((input, ctx) => transformIntensity(input, ctx, config)),
        displayValue: toDisplay,
    });
}
export function transformRPE(input, ctx) {
    if (input === '')
        return '';
    const parseResult = parseExactOrRange(input);
    if (!parseResult) {
        return invalidValue(ctx, tKey('rpeMustBeInRange'));
    }
    const { min, max } = parseResult;
    if (min > 10 || (isValidNumber(max) && max > 10)) {
        return invalidValue(ctx, tKey('rpeMustBeInRange'));
    }
    // range case
    if (isValidNumber(max)) {
        return {
            type: 'range',
            min,
            max,
        };
    }
    // exact case
    return { type: 'exact', value: min };
}
export function transformRep(input, ctx, config) {
    // Detect open-ended range "N+" (e.g. "5+" means "5 or more") before complex delimiter check
    const trimmed = input.trim();
    if (trimmed.endsWith('+') && trimmed.indexOf('+') === trimmed.length - 1) {
        const numPart = trimmed.slice(0, -1).trim();
        const value = parseIntNoNaN(numPart);
        if (isValidNumber(value) && value > 0) {
            return { type: 'range', min: value };
        }
    }
    const COMPLEX_DELIMITER = [':', '+'];
    const foundDelimiters = COMPLEX_DELIMITER.filter((delimiter) => input.includes(delimiter));
    if (foundDelimiters.length > 1) {
        return invalidValue(ctx, tKey('onlyOneDelimiterAllowed', {
            delimiters: COMPLEX_DELIMITER.join(', '),
        }));
    }
    const delimiter = foundDelimiters[0];
    if (delimiter) {
        const repStrings = input.split(delimiter);
        const reps = repStrings
            .map((rep) => parseIntNoNaN(rep.trim()))
            .filter(isValidNumber);
        if (reps.length !== repStrings.length) {
            return invalidValue(ctx, tKey('onlyNumbersAllowed'));
        }
        return {
            type: 'complex',
            delimiter,
            reps,
        };
    }
    return parseMetricValue(input, config, ctx);
}
export function transformSets(input, ctx) {
    if (input.trim().length === 0) {
        return invalidValue(ctx, tKey('setsCannotBeEmpty'));
    }
    const asNum = parseIntNoNaN(input.trim());
    // Can't be empty, negative, or a decimal
    if (!asNum || asNum < 1 || !Number.isInteger(asNum)) {
        return invalidValue(ctx, tKey('invalidSetValueMessage'));
    }
    return {
        type: 'exact',
        value: asNum,
    };
}
export function transformIntensity(input, ctx, config) {
    if (input === '')
        return '';
    if (config?.keywords.includes(input)) {
        return { type: 'keyword', value: input };
    }
    const units = [...zWeightUnit.options, '%'];
    let unit = null;
    let value = input;
    for (const possibleUnit of units) {
        if (input.endsWith(possibleUnit)) {
            unit = possibleUnit;
            value = input.slice(0, -possibleUnit.length);
            break;
        }
    }
    const parseResult = parseExactOrRange(value);
    if (!parseResult) {
        const weights = zWeightUnit.options.map((unit) => `"X${unit}"`).join(', ');
        return invalidValue(ctx, config && config.keywords.length > 0
            ? tKey('intensityShouldBePercentWeightOrKeyword', {
                weights,
                keywords: config.keywords.join(', '),
            })
            : tKey('intensityShouldBePercentOrWeight', { weights }));
    }
    const { min, max } = parseResult;
    const unitless = unit === null;
    const isPercentage = unit === '%' || unitless;
    // range case
    if (isValidNumber(max)) {
        if (isPercentage) {
            const percentageLoValue = unitless && min < 1 ? min * 100 : min;
            const percentageHighValue = unitless && max < 1 ? max * 100 : max;
            return {
                type: 'range',
                min: percentageLoValue,
                max: percentageHighValue,
                format: 'percentage',
                unit: '%',
            };
        }
        return {
            type: 'range',
            min,
            max,
            format: 'weight',
            unit: unit,
        };
    }
    // exact case
    if (isPercentage) {
        const percentageValue = unitless && min < 1 ? min * 100 : min;
        return {
            type: 'exact',
            format: 'percentage',
            value: percentageValue,
            unit: '%',
        };
    }
    else {
        return {
            type: 'exact',
            format: 'weight',
            value: min,
            unit: unit,
        };
    }
}
const isValidTempoPhaseValue = (val) => {
    if (val === 'X')
        return true;
    return isValidNumber(val);
};
export function transformTempo(input, ctx) {
    if (input === '')
        return '';
    const parts = input.includes('-') ? input.split('-') : input.split('');
    if (parts.length === 3 || parts.length === 4) {
        const [eccentric, pauseBottom, concentric, pauseTop] = parts.map((val) => val === 'X' ? 'X' : parseIntNoNaN(val));
        if (isValidTempoPhaseValue(eccentric) &&
            isValidTempoPhaseValue(pauseBottom) &&
            isValidTempoPhaseValue(concentric) &&
            (pauseTop === undefined || isValidTempoPhaseValue(pauseTop))) {
            return {
                eccentric,
                pauseBottom,
                concentric,
                ...(pauseTop !== undefined
                    ? { pauseTop, type: 'exact-4' }
                    : { type: 'exact-3' }),
            };
        }
    }
    invalidValue(ctx, tKey('tempoShouldBeInFormat'));
    return z.NEVER;
}
//# sourceMappingURL=index.js.map